import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import {
    ArcElement,
    BarController,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    LineController,
    LineElement,
    LinearScale,
    PointElement,
    Tooltip,
} from "chart.js";
import { Chart, Doughnut } from "react-chartjs-2";

import PanelTemplate from "../../../templates/Panel";
import PageHeader from "../../../components/PageHeader";
import Box from "../../../components/Box";
import BoxKpi from "../../../components/dashboard/BoxKpi";
import Button from "../../../components/Button";
import Icon from "../../../components/Icon";
import InputDate from "../../../components/InputDate";
import BillingCompanyFilter from "../BillingCompanyFilter";

import api from "../../../services/api";
import { hasPermission } from "../../../services/auth";
import { renderError } from "../../../helpers/errors";
import { convertIntToMoney, formatCurrencyShort } from "../../../helpers/conversions";
import { getStoredBillingCompany, storeBillingCompany, useBillingCompanies } from "../billingCompany";

ChartJS.register(ArcElement, BarController, BarElement, CategoryScale, LineController, LineElement, LinearScale, PointElement, Tooltip);

const PERIOD_PRESETS = [
    {
        key: 'this-month',
        label: 'Este mês',
        range: () => [moment().startOf('month'), moment().endOf('month')],
    },
    {
        key: 'last-month',
        label: 'Mês passado',
        range: () => [moment().subtract(1, 'month').startOf('month'), moment().subtract(1, 'month').endOf('month')],
    },
    {
        key: 'this-quarter',
        label: 'Este trimestre',
        range: () => [moment().startOf('quarter'), moment().endOf('quarter')],
    },
    {
        key: 'this-year',
        label: 'Este ano',
        range: () => [moment().startOf('year'), moment().endOf('year')],
    },
];

const DEFAULT_PRESET = PERIOD_PRESETS[0];

const FORECAST_OPTIONS = [
    { months: 6, label: 'Próximos 6 meses' },
    { months: 12, label: 'Próximos 12 meses' },
];

const COLOR_INCOME = '#1B9E77';
const COLOR_EXPENSE = '#E84C4E';
const COLOR_BALANCE = '#4D3DAB';
const AXIS_COLOR = '#7A7499';
const GRID_COLOR = '#EEECF7';

/**
 * Variação percentual entre o período e o anterior. Sem base não existe
 * variação: crescer a partir de zero não é "100% a mais", é começar.
 */
const variation = (amount, previous) => {
    if (!previous) return null;

    return ((amount - previous) / Math.abs(previous)) * 100;
};

const formatPercent = value => `${value.toFixed(1).replace('.', ',')}%`;

/**
 * Para receita e saldo, subir é bom; para despesa, é o contrário. Sem esse
 * parâmetro a seta verde apareceria celebrando um aumento de contas a pagar.
 */
const Variation = ({ amount, previous, positiveIsGood = true }) => {
    const value = variation(amount, previous);

    if (value === null) return <>Sem base no período anterior</>;
    if (value === 0) return <>Sem variação no período</>;

    const isUp = value > 0;
    const isGood = isUp === positiveIsGood;

    return (
        <>
            <span className={`overview-variation ${isGood ? 'is-good' : 'is-bad'}`}>
                <Icon name={isUp ? 'sort_asc' : 'sort_desc'} size={13} />
                {formatPercent(Math.abs(value))}
            </span>
            {' '}vs. período anterior
        </>
    );
};

export const FinanceOverview = () => {
    const [loading, setLoading] = useState(true);
    const [overview, setOverview] = useState(null);

    const [preset, setPreset] = useState(DEFAULT_PRESET.key);
    const [startDate, setStartDate] = useState(DEFAULT_PRESET.range()[0].format('YYYY-MM-DD'));
    const [endDate, setEndDate] = useState(DEFAULT_PRESET.range()[1].format('YYYY-MM-DD'));
    const [forecastMonths, setForecastMonths] = useState(FORECAST_OPTIONS[0].months);
    const [billingCompanyId, setBillingCompanyId] = useState(getStoredBillingCompany());

    const { billingCompanies } = useBillingCompanies();

    const navigate = useNavigate();

    const canManage = hasPermission('manage_finance');
    const periodIsValid = !!startDate && !!endDate && !moment(endDate).isBefore(startDate);

    // Empresa que saiu do cadastro continuaria filtrando um id inexistente e o
    // painel ficaria vazio sem explicar por quê.
    useEffect(() => {
        if (billingCompanies.length === 0 || !billingCompanyId) return;
        if (billingCompanies.some(company => company.id === billingCompanyId)) return;

        storeBillingCompany('');
        setBillingCompanyId('');
    }, [billingCompanies]);

    useEffect(() => {
        if (!periodIsValid) return;

        getOverview();
    }, [startDate, endDate, forecastMonths, billingCompanyId]);

    const getOverview = () => {
        setLoading(true);

        const params = new URLSearchParams({
            start_date: startDate,
            end_date: endDate,
            forecast_months: forecastMonths,
        });

        if (billingCompanyId) params.append('billing_company_id', billingCompanyId);

        api.get(`/finance-report/overview?${params.toString()}`)
            .then(res => setOverview(res.data.resource))
            .catch(renderError)
            .then(() => setLoading(false));
    };

    const handlePreset = item => {
        const [start, end] = item.range();

        setPreset(item.key);
        setStartDate(start.format('YYYY-MM-DD'));
        setEndDate(end.format('YYYY-MM-DD'));
    };

    const handleStartDate = value => {
        setPreset(null);
        setStartDate(value);
    };

    const handleEndDate = value => {
        setPreset(null);
        setEndDate(value);
    };

    const handleBillingCompany = id => {
        storeBillingCompany(id);
        setBillingCompanyId(id);
    };

    const indicators = overview?.indicators;
    const statement = overview?.income_statement;
    const reconciliation = overview?.reconciliation;
    const upcoming = overview?.upcoming ?? [];

    // Sem memo o `?? []` devolve um array novo a cada render e o gráfico
    // remonta o dataset inteiro à toa.
    const forecast = useMemo(() => overview?.forecast ?? [], [overview]);

    const forecastData = useMemo(() => ({
        labels: forecast.map(item => moment(item.month, 'YYYY-MM').locale('pt-br').format('MMM/YY')),
        datasets: [
            {
                type: 'bar',
                label: 'Entradas',
                data: forecast.map(item => item.income),
                backgroundColor: COLOR_INCOME,
                borderRadius: 4,
                maxBarThickness: 28,
                order: 2,
            },
            {
                type: 'bar',
                label: 'Saídas',
                data: forecast.map(item => item.expense),
                backgroundColor: COLOR_EXPENSE,
                borderRadius: 4,
                maxBarThickness: 28,
                order: 2,
            },
            {
                type: 'line',
                label: 'Saldo acumulado',
                data: forecast.map(item => item.balance),
                borderColor: COLOR_BALANCE,
                backgroundColor: COLOR_BALANCE,
                borderWidth: 2,
                pointRadius: 4,
                tension: 0.25,
                order: 1,
            },
        ],
    }), [forecast]);

    const forecastOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: 'index', intersect: false },
        plugins: {
            legend: { display: false },
            tooltip: {
                backgroundColor: '#1A1530',
                cornerRadius: 8,
                padding: 12,
                callbacks: {
                    label: context => `${context.dataset.label}: ${convertIntToMoney(context.parsed.y, true)}`,
                },
            },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { color: GRID_COLOR },
                ticks: { color: AXIS_COLOR },
            },
            y: {
                grid: { color: GRID_COLOR },
                border: { display: false },
                ticks: {
                    color: AXIS_COLOR,
                    callback: value => formatCurrencyShort(value),
                },
            },
        },
    }), []);

    const reconciliationData = useMemo(() => ({
        labels: ['Conciliados', 'Pendentes', 'Ignorados'],
        datasets: [{
            data: [reconciliation?.matched ?? 0, reconciliation?.pending ?? 0, reconciliation?.ignored ?? 0],
            backgroundColor: ['#1B9E77', '#E84C4E', '#DDD9F0'],
            borderWidth: 0,
            hoverOffset: 6,
        }],
    }), [reconciliation]);

    const reconciliationOptions = {
        cutout: '72%',
        maintainAspectRatio: false,
        plugins: { legend: { display: false } },
    };

    const dueLabel = dueDate => {
        const days = moment(dueDate).startOf('day').diff(moment().startOf('day'), 'days');

        if (days === 0) return 'Vence hoje';
        if (days === 1) return 'Vence amanhã';

        return `Vence em ${days} dias`;
    };

    return (
        <PanelTemplate id="finance-overview">
                <div className="finance-overview">
                <PageHeader
                    left={
                        <div>
                            <h1>Visão geral financeira</h1>
                            <span className="overview-subtitle">
                                Acompanhe seus números e tenha mais clareza para decidir todos os dias.
                            </span>
                        </div>
                    }
                    right={
                        <BillingCompanyFilter
                            billingCompanies={billingCompanies}
                            value={billingCompanyId}
                            change={handleBillingCompany}
                        />
                    }
                />

                <Box className={`overview-filter`}>
                    <div className="overview-toolbar">
                    <div className="finance-period-presets">
                        {PERIOD_PRESETS.map(item => (
                            <button
                                key={item.key}
                                type="button"
                                className={`period-preset${preset === item.key ? ' active' : ''}`}
                                onClick={() => handlePreset(item)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    <div className="finance-filters">
                        <div className="filter-field">
                            <InputDate label={`De`} format={`YYYY-MM-DD`} value={startDate} change={handleStartDate} />
                        </div>
                        <div className="filter-field">
                            <InputDate label={`Até`} format={`YYYY-MM-DD`} value={endDate} change={handleEndDate} />
                        </div>
                    </div>
                    </div>

                    {!periodIsValid &&
                        <span className="finance-period-error">A data final precisa ser igual ou posterior à inicial.</span>
                    }
                </Box>

                <div className="finance-overview-kpis">
                    <BoxKpi
                        icon={`money`}
                        accent={`teal`}
                        label={`Saldo atual`}
                        money={indicators?.balance.amount ?? 0}
                        loading={loading}
                        hint={indicators &&
                            <Variation amount={indicators.balance.amount} previous={indicators.balance.previous} />
                        }
                    />
                    <BoxKpi
                        icon={`sort_asc`}
                        accent={`cobalt`}
                        label={`A receber`}
                        money={indicators?.receivable.amount ?? 0}
                        loading={loading}
                        hint={indicators &&
                            <Variation amount={indicators.receivable.amount} previous={indicators.receivable.previous} />
                        }
                    />
                    <BoxKpi
                        icon={`sort_desc`}
                        accent={`coral`}
                        label={`A pagar`}
                        money={indicators?.payable.amount ?? 0}
                        loading={loading}
                        hint={indicators &&
                            <Variation
                                amount={indicators.payable.amount}
                                previous={indicators.payable.previous}
                                positiveIsGood={false}
                            />
                        }
                    />
                    <BoxKpi
                        icon={`chart`}
                        accent={`brand`}
                        label={`Resultado do período`}
                        money={indicators?.result.amount ?? 0}
                        loading={loading}
                        hint={indicators &&
                            <Variation amount={indicators.result.amount} previous={indicators.result.previous} />
                        }
                    />
                    <BoxKpi
                        icon={`alert`}
                        accent={indicators?.overdue.amount > 0 ? 'amber' : 'brand'}
                        label={`Em atraso`}
                        money={indicators?.overdue.amount ?? 0}
                        loading={loading}
                        hint={indicators &&
                            `${indicators.overdue.count} título${indicators.overdue.count === 1 ? '' : 's'} · ${formatPercent(indicators.overdue.share)} do a receber`
                        }
                    />
                </div>

                <div className="finance-overview-columns">
                    <Box
                        title={`Fluxo de caixa projetado`}
                        tools={
                            <Button
                                type={`link`}
                                size={`small`}
                                text={`Ver fluxo de caixa`}
                                action={() => navigate('/finance/cash-flow')}
                            />
                        }
                    >
                        <div className="overview-chart-toolbar">
                            <div className="overview-legend">
                                <span><i style={{ backgroundColor: COLOR_INCOME }} />Entradas</span>
                                <span><i style={{ backgroundColor: COLOR_EXPENSE }} />Saídas</span>
                                <span><i style={{ backgroundColor: COLOR_BALANCE }} />Saldo acumulado</span>
                            </div>
                            <div className="finance-view-modes">
                                {FORECAST_OPTIONS.map(option => (
                                    <button
                                        key={option.months}
                                        type="button"
                                        className={`view-mode${forecastMonths === option.months ? ' active' : ''}`}
                                        onClick={() => setForecastMonths(option.months)}
                                    >
                                        {option.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="overview-chart">
                            {forecast.length === 0 && !loading
                                ? <div className="overview-empty">Sem títulos previstos para os próximos meses.</div>
                                : <Chart type="bar" data={forecastData} options={forecastOptions} />
                            }
                        </div>
                    </Box>

                    <Box
                        title={`Próximos vencimentos`}
                        tools={
                            <Button
                                type={`link`}
                                size={`small`}
                                text={`Ver todos`}
                                action={() => navigate('/finance/statement?situation=open')}
                            />
                        }
                        className={`no-padding`}
                    >
                        {upcoming.length === 0
                            ? <div className="overview-empty">{loading ? 'Carregando...' : 'Nada a vencer por aqui.'}</div>
                            : <ul className="overview-due-list">
                                {upcoming.map(entry => (
                                    <li key={entry.id}>
                                        <span className="due-date">
                                            <strong>{moment(entry.due_date).format('DD')}</strong>
                                            {moment(entry.due_date).locale('pt-br').format('MMM').toUpperCase()}
                                        </span>
                                        <span className="due-description">
                                            <strong>{entry.counterpart ?? entry.description}</strong>
                                            <small>
                                                {entry.reference_code ? `${entry.reference_code} • ` : ''}
                                                {entry.type === 'receivable' ? 'Contas a receber' : 'Contas a pagar'}
                                            </small>
                                        </span>
                                        <span className={`due-amount ${entry.type === 'receivable' ? 'value-positive' : 'value-negative'}`}>
                                            {convertIntToMoney(entry.amount, true)}
                                        </span>
                                        <span className="due-badge">{dueLabel(entry.due_date)}</span>
                                    </li>
                                ))}
                            </ul>
                        }
                    </Box>
                </div>

                <div className="finance-overview-columns is-thirds">
                    <Box
                        title={`Resumo de conciliação bancária`}
                        tools={
                            <Button
                                type={`link`}
                                size={`small`}
                                text={`Ver conciliação`}
                                action={() => navigate('/finance/reconciliation')}
                            />
                        }
                    >
                        <div className="overview-reconciliation">
                            <div className="reconciliation-donut">
                                <Doughnut data={reconciliationData} options={reconciliationOptions} />
                                <div className="donut-center">
                                    <strong>{reconciliation?.rate ?? 0}%</strong>
                                    <span>conciliado</span>
                                </div>
                            </div>
                            <ul className="reconciliation-legend">
                                <li><i className="dot-matched" />Conciliados<strong>{reconciliation?.matched ?? 0}</strong></li>
                                <li><i className="dot-pending" />Pendentes<strong>{reconciliation?.pending ?? 0}</strong></li>
                                <li><i className="dot-ignored" />Ignorados<strong>{reconciliation?.ignored ?? 0}</strong></li>
                            </ul>
                        </div>
                        {reconciliation?.pending > 0 &&
                            <div className="overview-notice">
                                <Icon name="alert" size={16} />
                                Você tem {reconciliation.pending} lançamentos pendentes de conciliação.
                            </div>
                        }
                    </Box>

                    <Box
                        title={`Resultado do período`}
                        tools={
                            <Button
                                type={`link`}
                                size={`small`}
                                text={`Ver DRE completo`}
                                action={() => navigate('/finance/income-statement')}
                            />
                        }
                    >
                        <ul className="overview-statement">
                            <li>
                                <span>Receitas</span>
                                <strong className="value-positive">{convertIntToMoney(statement?.revenue ?? 0, true)}</strong>
                            </li>
                            <li>
                                <span>Despesas</span>
                                <strong className="value-negative">{convertIntToMoney(statement?.expense ?? 0, true)}</strong>
                            </li>
                            <li>
                                <span>Margem</span>
                                <strong>{formatPercent(statement?.margin ?? 0)}</strong>
                            </li>
                            <li className="is-result">
                                <span>Resultado do período</span>
                                <strong className={statement?.result >= 0 ? 'value-positive' : 'value-negative'}>
                                    {convertIntToMoney(statement?.result ?? 0, true)}
                                </strong>
                            </li>
                        </ul>
                    </Box>

                    <Box title={`Ações rápidas`}>
                        <div className="overview-actions">
                            {canManage &&
                                <button type="button" onClick={() => navigate('/finance/receivables')}>
                                    <Icon name="plus-circle" size={18} />Nova conta a receber
                                </button>
                            }
                            {canManage &&
                                <button type="button" onClick={() => navigate('/finance/payables')}>
                                    <Icon name="plus-circle" size={18} />Nova conta a pagar
                                </button>
                            }
                            <button type="button" onClick={() => navigate('/fiscal/documents')}>
                                <Icon name="scale" size={18} />Emitir nota fiscal
                            </button>
                            <button type="button" onClick={() => navigate('/finance/reconciliation')}>
                                <Icon name="import" size={18} />Importar extrato
                            </button>
                            <button type="button" onClick={() => navigate('/report')}>
                                <Icon name="chart" size={18} />Ver relatórios
                            </button>
                            <button type="button" onClick={() => navigate('/finance/automations')}>
                                <Icon name="zap" size={18} />Configurar automações
                            </button>
                        </div>
                    </Box>
                </div>
            </div>
        </PanelTemplate>
    );
};

export default FinanceOverview;
